import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
/**
 * Speed Test Hooks - Hooks personalizados para gerenciar testes de velocidade
 *
 * Utiliza React Query para gerenciamento de cache e estado das requisições.
 * Fornece hooks para criar, deletar, listar e obter métricas de testes.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';
import {
  speedTestService,
  CreateSpeedTestRequest,
  SpeedTestResponse,
  SpeedTestMetrics,
  GetMetricsParams,
} from '@/services/speedTestService';

// ===== QUERY KEYS =====
export const speedTestKeys = {
  all: ['speedTests'] as const,
  byUser: (userId: string) => [...speedTestKeys.all, 'user', userId] as const,
  detail: (speedTestId: string) => [...speedTestKeys.all, 'detail', speedTestId] as const,
  metrics: (speedTestId: string, inicio: string, fim: string) =>
    [...speedTestKeys.all, 'metrics', speedTestId, inicio, fim] as const,
  video: (speedTestId: string) => [...speedTestKeys.all, 'video', speedTestId] as const,
};

// ===== HOOKS DE QUERY =====

/**
 * Hook para buscar todos os testes de velocidade de um usuário
 *
 * @param userId - ID do usuário
 * @param enabled - Se a query deve ser executada (default: true)
 * @returns Query com lista de testes
 */
export const useSpeedTestsByUser = (userId: string, enabled = true) => {
  useLanguage();
  return useQuery({
    queryKey: speedTestKeys.byUser(userId),
    queryFn: () => speedTestService.findSpeedTestsByUserId(userId),
    enabled: enabled && !!userId,
    staleTime: 5 * 60 * 1000, // 5 minutos
  });
};

/**
 * Hook para buscar métricas de um teste de velocidade
 *
 * @param params - Parâmetros da busca (speedTestId, inicio, fim)
 * @param enabled - Se a query deve ser executada (default: true)
 * @returns Query com métricas do teste
 */
export const useSpeedTestMetrics = (params: GetMetricsParams, enabled = true) => {
  useLanguage();
  return useQuery({
    queryKey: speedTestKeys.metrics(params.speedTestId, params.inicio, params.fim),
    queryFn: () => speedTestService.getSpeedTestMetrics(params),
    enabled: enabled && !!params.speedTestId && !!params.inicio && !!params.fim,
    staleTime: 2 * 60 * 1000, // 2 minutos
  });
};

/**
 * Hook para selecionar vídeo do teste de velocidade
 *
 * @param speedTestId - ID do teste
 * @param enabled - Se a query deve ser executada (default: true)
 * @returns Query com vídeo selecionado
 */
export const useSpeedTestVideo = (speedTestId: string, enabled = true) => {
  useLanguage();
  return useQuery({
    queryKey: speedTestKeys.video(speedTestId),
    queryFn: () => speedTestService.selectSpeedTestVideo(speedTestId),
    enabled: enabled && !!speedTestId,
    staleTime: 10 * 60 * 1000, // 10 minutos
  });
};

// ===== HOOKS DE MUTATION =====

/**
 * Hook para criar um novo teste de velocidade
 *
 * @returns Mutation para criar teste
 */
export const useCreateSpeedTest = () => {
  useLanguage();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (data: CreateSpeedTestRequest) =>
      speedTestService.createSpeedTest(data.videoId, data.userId, data.speeds, data.endDate),
    onSuccess: (_, variables) => {
      // Invalida cache do usuário para recarregar lista
      queryClient.invalidateQueries({
        queryKey: speedTestKeys.byUser(variables.userId),
      });

      toast({
        title: translateInterfaceText("Teste criado!"),
        description: translateInterfaceText("Teste de velocidade criado com sucesso."),
        variant: 'default',
      });
    },
    onError: (error: Error) => {
      toast({
        title: translateInterfaceText("Erro ao criar teste"),
        description: error.message || translateInterfaceText("Não foi possível criar o teste de velocidade."),
        variant: 'destructive',
      });
    },
  });
};

/**
 * Hook para deletar um teste de velocidade
 *
 * @returns Mutation para deletar teste
 */
export const useDeleteSpeedTest = () => {
  useLanguage();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (speedTestId: string) => speedTestService.deleteSpeedTest(speedTestId),
    onSuccess: () => {
      // Invalida todos os caches relacionados a speed tests
      queryClient.invalidateQueries({
        queryKey: speedTestKeys.all,
      });

      toast({
        title: translateInterfaceText("Teste deletado!"),
        description: translateInterfaceText("Teste de velocidade deletado com sucesso."),
        variant: 'default',
      });
    },
    onError: (error: Error) => {
      toast({
        title: translateInterfaceText("Erro ao deletar teste"),
        description: error.message || translateInterfaceText("Não foi possível deletar o teste de velocidade."),
        variant: 'destructive',
      });
    },
  });
};

// ===== HOOK COMPOSTO =====

/**
 * Hook composto que agrupa funcionalidades comuns de speed test
 * Útil para componentes que precisam de múltiplas operações
 *
 * @param userId - ID do usuário
 * @returns Objeto com queries e mutations prontas para uso
 */
export const useSpeedTest = (userId: string) => {
  useLanguage();
  const speedTestsQuery = useSpeedTestsByUser(userId);
  const createMutation = useCreateSpeedTest();
  const deleteMutation = useDeleteSpeedTest();

  return {
    // Queries
    speedTests: speedTestsQuery.data || [],
    isLoading: speedTestsQuery.isLoading,
    isError: speedTestsQuery.isError,
    error: speedTestsQuery.error,
    refetch: speedTestsQuery.refetch,

    // Mutations
    createSpeedTest: createMutation.mutate,
    deleteSpeedTest: deleteMutation.mutate,
    isCreating: createMutation.isPending,
    isDeleting: deleteMutation.isPending,
  };
};

export default useSpeedTest;
